import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ListChecks } from 'lucide-react';
import { cn } from '../lib/utils';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Top-nav button that doubles as the daily count badge for outstanding follow-ups.
// Click → /follow-ups. Refreshes every 60s + after window focus.
const FollowUpsBadge = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ overdue: 0, due_today: 0, due_this_week: 0, total: 0 });
  const timerRef = useRef(null);

  const load = async () => {
    if (!token) return;
    try {
      const params = new URLSearchParams();
      // Default to current user's follow-ups (admins can switch on the page itself)
      if (user?.id && user?.role !== 'admin') params.set('assigned_to', user.id);
      const res = await fetch(`${backendUrl}/api/nurture/follow-ups/count?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const d = await res.json();
        setCounts(d);
      }
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 60_000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener('focus', onFocus);
    };
  // eslint-disable-next-line
  }, [token, user?.id]);

  // Visual state: rose if anything overdue, amber if today, slate otherwise.
  const tone = counts.overdue > 0
    ? 'text-rose-600 dark:text-rose-300 hover:text-rose-700 dark:hover:text-rose-200'
    : counts.due_today > 0
      ? 'text-amber-600 dark:text-amber-300 hover:text-amber-700 dark:hover:text-amber-200'
      : 'text-slate-500 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white';

  const dotTone = counts.overdue > 0
    ? 'bg-rose-500'
    : counts.due_today > 0
      ? 'bg-amber-500'
      : 'bg-emerald-500';

  const showCount = counts.overdue > 0 ? counts.overdue : counts.due_today;

  return (
    <button
      onClick={() => navigate('/lead-nurture')}
      className={cn(
        'relative flex items-center gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800/70 transition-colors',
        tone,
      )}
      title={`${counts.overdue} overdue · ${counts.due_today} due today · ${counts.due_this_week} due this week`}
      aria-label="Follow-ups"
      data-testid="header-followups-btn"
    >
      <ListChecks className="w-4 h-4 sm:w-5 sm:h-5" />
      <span className="hidden md:inline text-sm font-medium">Follow-ups</span>
      {showCount > 0 && (
        <span
          className={cn(
            'absolute -top-1 -right-1 sm:static sm:ml-1 inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] sm:min-w-[1.25rem] sm:h-[1.25rem] px-1 rounded-full text-[10px] sm:text-xs font-bold text-white shadow-md',
            dotTone,
          )}
          data-testid="followups-badge-count"
        >
          {showCount > 99 ? '99+' : showCount}
        </span>
      )}
    </button>
  );
};

export default FollowUpsBadge;
